import { ProjectDonationAmount } from '@/app/api';
import { countryConfig } from '@/app/utils/countryConfig';
import { envs } from '@/app/utils/envs';
import { getAmountInRespectiveCurrency } from '@/app/utils/numbers';

const DEFAULT_AMOUNT_EXPERIMENT_RATIO = 0.25;

const GROUPS_COUNT = 20;

// Project can have custom donation amount options
const SUPPORTED_DONATION_AMOUNT_OPTIONS_LENGTHS_FOR_AB_TESTING = [5, 6];

export const AB_GROUP_COOKIE_KEY = 'AB_GROUP';

//Random number between 1 and 20
export const getRandomAbGroup = () => {
  return `${Math.floor(Math.random() * GROUPS_COUNT) + 1}`;
};

export const isAbGroupValid = (group: string | undefined) => {
  if (!group) {
    return false;
  }
  const parsedGroup = Number(group);

  if (isNaN(parsedGroup)) {
    return false;
  }

  return parsedGroup >= 1 && parsedGroup <= GROUPS_COUNT;
};

export const isGroupInHigherDefaultAmountExperiment = (group: string) => {
  if (!isAbGroupValid(group) || envs.isInCypressTesting) {
    return false;
  }

  const parsedGroup = Number(group);

  return parsedGroup <= GROUPS_COUNT * DEFAULT_AMOUNT_EXPERIMENT_RATIO;
};

/*
  If the user is 25% of the users he will get higher default amount by one option
  the result should be how often the user will change the default amount
*/

export const getDefaultAmountBasedOnGroup = (
  group: string | undefined,
  values: Array<ProjectDonationAmount> | null,
): number | undefined => {
  const fallBackAmount = getAmountInRespectiveCurrency(values?.[0]?.amountInCents || 0);

  if (!values?.length || !group) {
    return fallBackAmount;
  }

  if (!SUPPORTED_DONATION_AMOUNT_OPTIONS_LENGTHS_FOR_AB_TESTING.includes(values.length)) {
    return fallBackAmount;
  }

  const sortedOptionFromLowestToHighest = [...values.sort((a, b) => a.amountInCents - b.amountInCents)];

  if (isGroupInHigherDefaultAmountExperiment(group)) {
    return getAmountInRespectiveCurrency(sortedOptionFromLowestToHighest[2].amountInCents);
  }

  return getAmountInRespectiveCurrency(sortedOptionFromLowestToHighest[1].amountInCents);
};

export const getTipOptionsForHigherAmountBasedOnGroup = (group: string | undefined) => {
  const defaultOptions = [
    { value: 0, label: '0%', noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
    { value: 3, label: '3%', noteTranslationKey: 'components.TipForDonioInput.weReallyAppreciateYourSupport' },
    { value: 5, label: '5%', noteTranslationKey: 'components.TipForDonioInput.youHelpUsCoverBankingFees' },
    { value: 11, label: '11%', noteTranslationKey: 'components.TipForDonioInput.youAreReallySuper' },
  ];

  if (group === undefined || !isAbGroupValid(group) || envs.isInCypressTesting) {
    return defaultOptions;
  }

  const parsedGroup = Number(group);
  const isTestingGroup = parsedGroup >= 14 && parsedGroup <= 20;

  if (!isTestingGroup) {
    return defaultOptions;
  }

  return [
    { value: 0, label: '0%', noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
    { value: 3, label: '3%', noteTranslationKey: 'components.TipForDonioInput.weReallyAppreciateYourSupport' },
    { value: 5, label: '5%', noteTranslationKey: 'components.TipForDonioInput.youHelpUsCoverBankingFees' },
    { value: 11, label: '11%', noteTranslationKey: 'components.TipForDonioInput.youAreReallySuper' },
    { value: 17, label: '17%', noteTranslationKey: 'components.TipForDonioInput.wowMessage' },
  ];
};

export const getTipOptionsForLowerAmountBasedOnGroup = (group: string | undefined) => {
  const defaultOptions = [
    { value: 0, label: '0%', noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
    { value: 5, label: '5%', noteTranslationKey: 'components.TipForDonioInput.weReallyAppreciateYourSupport' },
    { value: 11, label: '11%', noteTranslationKey: 'components.TipForDonioInput.youHelpUsCoverBankingFees' },
    { value: 25, label: '25%', noteTranslationKey: 'components.TipForDonioInput.wowMessage' },
  ];

  if (group === undefined || !isAbGroupValid(group) || envs.isInCypressTesting) {
    return defaultOptions;
  }

  const parsedGroup = Number(group);
  const isTestingGroup = parsedGroup >= 14 && parsedGroup <= 20;

  if (!isTestingGroup) {
    return defaultOptions;
  }

  const testingGroupOptions = [
    { value: 0, label: '0%', noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
    { value: 5, label: '5%', noteTranslationKey: 'components.TipForDonioInput.weReallyAppreciateYourSupport' },
    { value: 7, label: '7%', noteTranslationKey: 'components.TipForDonioInput.lucky7Percents' },
    { value: 11, label: '11%', noteTranslationKey: 'components.TipForDonioInput.youHelpUsCoverBankingFees' },
    { value: 17, label: '17%', noteTranslationKey: 'components.TipForDonioInput.youAreReallySuper' },
    { value: 25, label: '25%', noteTranslationKey: 'components.TipForDonioInput.wowMessage' },
  ];

  return testingGroupOptions;
};

export const isUserInAmountBasedTipOptions = (group: string) => {
  if (!group || envs.isInCypressTesting) {
    return false;
  }
  const parsedGroup = Number(group);

  if (isNaN(parsedGroup)) {
    return false;
  }

  return parsedGroup >= 7 && parsedGroup <= 13;
};

export const getMaxAmountBasedTipOption = () => {
  return countryConfig.amountBasedTipOptions[countryConfig.amountBasedTipOptions.length - 1].to;
};

export const getDefaultAmountBasedValue = (amount: number) => {
  const group = countryConfig.amountBasedTipOptions.find((option) => {
    return amount >= option.from && amount <= option.to;
  });

  if (!group) {
    return undefined;
  }

  return group.default;
};
